import { defineStore } from 'pinia'
import { markRaw } from 'vue'
import { StreamChat, type Channel, type MessageResponse } from 'stream-chat'
import { useUserStore } from './user'
import { System } from '@/router/config/roles'
import { i18n } from '@/locales'

export type ChatChannel = Channel
export type ChatMessage = MessageResponse

/**
 * 频道上记录的双方公司。
 *
 * 公司信息本来只挂在对方的 Stream 用户对象上（由服务端 upsert 写入），
 * 但那份数据一个账号只有一份、而且要等对方登录过才有，会出现空白或写成别的公司。
 * 发起会话时我方从联系人/搜索接口就能拿到对方公司，直接固化到频道上更可靠。
 *
 * 一条会话固定是「企业方 ↔ 猎头方」，所以两边的公司都记下来，各自读对面那个。
 */
export interface ChannelCompanyData {
  /** 企业方企业 id */
  enterprise_id: string
  /** 企业方企业名 */
  enterprise_name: string
  /** 猎头方公司 id */
  agency_id: string
  /** 猎头方公司名 */
  agency_name: string
}

/** 发起 1v1 会话所需的对方信息 */
export interface DirectChannelPeer {
  /** 对方的 Stream 用户 id，服务端下发（streamChatId），前端不可自行拼接 */
  chatUserId: string
  /** 对方所属公司 id */
  companyId: string | number
  /** 对方所属公司名 */
  companyName: string
}

function resolveStreamChatApiKey(): string {
  // 允许通过环境变量显式指定（建议在 .env.production / .env.test 等配置）
  const fromEnv = import.meta.env.VITE_STREAM_CHAT_API_KEY as string | undefined
  return (fromEnv ?? '').trim()
}

/**
 * 合作企业 id 对齐：企业列表 item.id 为 number，频道 custom.company_id 为 string，
 * 统一成同一字符串再放入 Set / has 比对。
 */
export function normalizeCoopCompanyId(raw: unknown): string {
  if (raw === null || raw === undefined) return ''
  if (typeof raw === 'number' && Number.isFinite(raw)) {
    return String(Math.trunc(raw))
  }
  const s = String(raw).trim()
  if (!s) return ''
  if (/^\d+$/.test(s)) return String(parseInt(s, 10))
  return s
}

const MESSAGE_PAGE_SIZE = 25

// StreamChat 客户端实例（模块级，不放入响应式）
let _client: StreamChat | null = null

/** 当前右侧消息区绑定的 message.new 监听（切换会话时必须 off，否则会串频道写入 messages） */
let _activeChannelForMessages: ChatChannel | null = null
let _activeChannelMessageNewHandler: ((event: { message?: ChatMessage }) => void) | null = null

function detachActiveChannelMessageListener() {
  if (_activeChannelForMessages && _activeChannelMessageNewHandler) {
    _activeChannelForMessages.off('message.new', _activeChannelMessageNewHandler)
  }
  _activeChannelForMessages = null
  _activeChannelMessageNewHandler = null
}

interface ChatState {
  // 连接
  apiKey: string
  userId: string
  token: string
  connected: boolean
  connecting: boolean
  // 未读
  unreadTotal: number
  // 频道列表
  channels: ChatChannel[]
  channelsLoading: boolean
  // 当前活跃频道
  activeChannel: ChatChannel | null
  // 消息
  messages: ChatMessage[]
  messageLoading: boolean
  hasMoreMessages: boolean
}

export const useChatStore = defineStore('chat', {
  state: (): ChatState => ({
    apiKey: resolveStreamChatApiKey(),
    userId: '',
    token: '',
    connected: false,
    connecting: false,
    unreadTotal: 0,
    channels: [],
    channelsLoading: false,
    activeChannel: null,
    messages: [],
    messageLoading: false,
    hasMoreMessages: false,
  }),

  getters: {
    /** StreamChat 客户端实例（非响应式） */
    client: (): StreamChat | null => _client,
  },

  actions: {
    /** 手动计算所有频道的总未读数 */
    _updateUnreadTotal() {
      let total = 0
      for (const ch of this.channels) {
        total += ch.countUnread()
      }
      console.log('[Chat] _updateUnreadTotal calculated:', total)
      this.unreadTotal = total
    },

    // ─── 连接 ──────────────────────────────────────────────────────────────────

    async init() {
      if (this.connected && _client?.userID) return
      if (this.connecting) return

      this.connecting = true
      const userStore = useUserStore()
      try {
        // chatToken 是服务端按 streamChatId 签的，两者必须配套，前端不能自己拼 id
        this.userId = userStore.userChatUserId
        this.token = userStore.userChatToken
        if (!this.userId || !this.token) {
          throw new Error('缺少 streamChatId 或 chatToken，无法连接聊天服务')
        }
        //console.log(this.userId, this.token)

        if (!_client) {
          _client = new StreamChat(this.apiKey)
        }

        await _client.connectUser(
          {
            id: this.userId,
            name: userStore.userName,
            image: userStore.userAvatar || undefined,
          },
          this.token,
        )

        this.connected = true
        this.unreadTotal = _client.user?.total_unread_count ?? 0

        // 全局未读监听
        _client.on('notification.message_new', event => {
          console.log('[Chat] notification.message_new, unread:', event.total_unread_count)
          this.unreadTotal = event.total_unread_count ?? this.unreadTotal
        })
        _client.on('notification.mark_read', event => {
          console.log('[Chat] notification.mark_read, unread:', event.total_unread_count)
          this.unreadTotal = event.total_unread_count ?? this.unreadTotal
          this.channels = [...this.channels]
        })
        _client.on('notification.mark_unread', event => {
          console.log('[Chat] notification.mark_unread, unread:', event.total_unread_count)
          // 优先用全局事件的未读数（比频道 countUnread 更准确）
          if (event.total_unread_count !== undefined) {
            this.unreadTotal = event.total_unread_count
          } else {
            this._updateUnreadTotal()
          }
          this.channels = [...this.channels]
        })
        // 监听频道级已读事件（某些情况下 notification.mark_read 不触发）
        _client.on('message.read', () => {
          console.log('[Chat] global message.read, unread:', _client?.user?.total_unread_count)
          this.unreadTotal = _client?.user?.total_unread_count ?? 0
        })
      } catch (e) {
        this.connected = false
        throw e
      } finally {
        this.connecting = false
      }
    },

    async disconnect() {
      detachActiveChannelMessageListener()
      if (_client) {
        await _client.disconnectUser()
        _client = null
      }
      this.$reset()
    },

    // ─── 频道列表 ───────────────────────────────────────────────────────────────

    async fetchAllChannels() {
      if (!_client) return
      this.channelsLoading = true
      try {
        const filter = { type: 'messaging', members: { $in: [_client.userID!] } }
        const sort = [{ last_message_at: -1 as const }]
        const options = { watch: true, state: true, message_limit: 20 }

        const result = await _client.queryChannels(filter, sort, options)
        // Channel 对象不能被 Vue 深度代理，用 markRaw 包裹
        this.channels = result.map(ch => markRaw(ch))
        result.forEach(ch => this._listenChannel(ch))
        this._updateUnreadTotal()
      } finally {
        this.channelsLoading = false
      }
    },

    async fetchUnReadChannels() {
      if (!_client) return
      this.channelsLoading = true
      try {
        const filter = { type: 'messaging', members: { $in: [_client.userID!] }, has_unread: true }
        const sort = [{ last_message_at: -1 as const }]
        const options = { watch: true, state: true, message_limit: 20 }

        const result = await _client.queryChannels(filter, sort, options)
        // Channel 对象不能被 Vue 深度代理，用 markRaw 包裹
        this.channels = result.map(ch => markRaw(ch))
        result.forEach(ch => this._listenChannel(ch))
        this._updateUnreadTotal()
      } finally {
        this.channelsLoading = false
      }
    },

    _listenChannel(ch: ChatChannel) {
      ch.on('message.new', () => {
        const idx = this.channels.findIndex(c => c.cid === ch.cid)
        if (idx < 0) {
          this.channels = [...this.channels]
          return
        }

        const isPinned = (c: ChatChannel) => !!(c.state.membership as Record<string, unknown>)?.pinned_at

        if (isPinned(ch)) {
          // 置顶频道：在置顶区内提到最前
          this.channels.splice(idx, 1)
          this.channels.unshift(markRaw(ch))
        } else {
          // 非置顶频道：插到所有置顶频道的后面
          this.channels.splice(idx, 1)
          const firstUnpinnedIdx = this.channels.findIndex(c => !isPinned(c))
          const insertAt = firstUnpinnedIdx === -1 ? 0 : firstUnpinnedIdx
          this.channels.splice(insertAt, 0, markRaw(ch))
        }
        this.channels = [...this.channels]
        // 收到新消息时也更新全局未读数
        this._updateUnreadTotal()
      })
      // markRead 后触发重渲染，更新未读角标
      ch.on('message.read', () => {
        this.channels = [...this.channels]
        this._updateUnreadTotal()
      })
      // markUnread 后也更新未读数
      ch.on('message.unread', () => {
        this.channels = [...this.channels]
        this._updateUnreadTotal()
      })
    },

    // ─── 打开频道 ───────────────────────────────────────────────────────────────

    async openChannel(channel: ChatChannel) {
      if (this.activeChannel?.cid === channel.cid) return

      detachActiveChannelMessageListener()

      this.activeChannel = markRaw(channel)
      this.messages = []
      this.hasMoreMessages = false
      await this._loadMessages(true)
      console.log('[Chat] markRead called for channel:', channel.cid)
      await channel.markRead()
      this._updateUnreadTotal()

      const cid = channel.cid
      let lastMarkReadAt = 0
      const handler = (event: { message?: ChatMessage }) => {
        // 已切到其他会话时，忽略非当前频道推送（与 detach 双保险）
        if (this.activeChannel?.cid !== cid) return
        if (!event.message) return
        const exists = this.messages.some(m => m.id === event.message!.id)
        if (!exists) {
          this.messages.push(event.message as ChatMessage)
        }

        // 用户停留在当前会话时，收到新消息应立即清掉未读角标。
        // 这里做一个轻量节流，避免消息量很大时频繁打接口。
        const now = Date.now()
        if (now - lastMarkReadAt < 800) return
        lastMarkReadAt = now
        void channel.markRead()
          .then(() => {
            // 触发频道列表重渲染（channel 内部 unread 变化不一定会触发 Vue 更新）
            this.channels = [...this.channels]
            this._updateUnreadTotal()
          })
          .catch(() => {
            // markRead 失败不影响消息展示
          })
      }
      _activeChannelForMessages = channel
      _activeChannelMessageNewHandler = handler
      channel.on('message.new', handler)
    },

    // ─── 消息加载 ───────────────────────────────────────────────────────────────

    async _loadMessages(initial = false) {
      if (!this.activeChannel) return
      this.messageLoading = true
      try {
        const options: Record<string, any> = { limit: MESSAGE_PAGE_SIZE }
        if (!initial && this.messages.length > 0) {
          options.id_lt = this.messages[0].id
        }
        const result = await this.activeChannel.query({ messages: options })
        const fetched = (result.messages ?? []) as ChatMessage[]
        if (initial) {
          this.messages = fetched
        } else {
          this.messages = [...fetched, ...this.messages]
        }
        this.hasMoreMessages = fetched.length === MESSAGE_PAGE_SIZE
      } finally {
        this.messageLoading = false
      }
    },

    async loadMoreMessages() {
      if (!this.hasMoreMessages || this.messageLoading) return
      await this._loadMessages(false)
    },

    /** 组装频道上的双方公司：按「我是哪一边」把我方/对方摆到对应字段 */
    _buildChannelCompanyData(peer: DirectChannelPeer): ChannelCompanyData {
      const userStore = useUserStore()
      const isBiz = userStore.userSystem === System.business
      const myId = normalizeCoopCompanyId(userStore.userEnterpriseId)
      const myName = userStore.profile?.enterpriseName ?? ''
      const peerId = normalizeCoopCompanyId(peer.companyId)
      return {
        enterprise_id: isBiz ? myId : peerId,
        enterprise_name: isBiz ? myName : peer.companyName,
        agency_id: isBiz ? peerId : myId,
        agency_name: isBiz ? peer.companyName : myName,
      }
    },

    /**
     * 补齐频道上缺失的公司字段。
     * 已存在的频道不会因为 watch() 就写入创建时传的自定义数据，改造前建的老会话需要在这里补。
     * 只补「缺失」的字段：已有值不覆盖，避免两端打开同一条会话时来回改写。
     */
    async _ensureChannelCompanyData(channel: ChatChannel, company: ChannelCompanyData) {
      const data = (channel.data ?? {}) as Record<string, unknown>
      const keys = Object.keys(company) as (keyof ChannelCompanyData)[]
      const missing = keys.filter(key => !data[key] && company[key])
      if (!missing.length) return
      const patch: Partial<ChannelCompanyData> = {}
      missing.forEach(key => { patch[key] = company[key] })
      try {
        await channel.updatePartial({ set: patch })
      } catch (e) {
        // 没有改频道的权限等情况下，退回到读对方用户上的公司，不影响会话正常使用
        console.warn('[Chat] 补写频道企业信息失败:', e)
      }
    },

    /** 打开或创建与指定用户的 1v1 会话 */
    async openDirectChannel(peer: DirectChannelPeer) {
      if (!_client || !_client.userID) return
      // 1v1 会话，成员为当前用户 + 目标用户
      const members = [_client.userID, peer.chatUserId]
      const company = this._buildChannelCompanyData(peer)
      const channel = _client.channel('messaging', { members, ...company })
      await channel.watch()
      await this._ensureChannelCompanyData(channel, company)

      // 放到频道列表顶部
      const existingIdx = this.channels.findIndex(c => c.cid === channel.cid)
      if (existingIdx >= 0) {
        this.channels.splice(existingIdx, 1)
      }
      this.channels.unshift(markRaw(channel))

      await this.openChannel(channel)
    },

    // ─── 更新频道自定义数据 ──────────────────────────────────────────────────────

    /** 更新指定频道的自定义数据（使用 updatePartial，只修改传入字段） */
    async updateChannelData(channel: ChatChannel, data: Record<string, unknown>) {
      await channel.updatePartial({ set: data })
      // 触发列表重渲染，使 getChannelCompany 等 getter 读到最新值
      this.channels = [...this.channels]
    },

    // ─── 发送消息 ───────────────────────────────────────────────────────────────

    async sendMessage(text: string, attachments: File[] = []) {
      if (!this.activeChannel) return

      const uploadedAttachments = await Promise.all(
        attachments.map(async file => {
          const isImage = file.type.startsWith('image/')
          const res = await this.activeChannel!.sendFile(file)
          return {
            type: isImage ? 'image' : 'file',
            asset_url: res.file,
            ...(isImage ? { thumb_url: res.file } : {}),
            title: file.name,
            file_size: file.size,
            mime_type: file.type,
          }
        }),
      )

      await this.activeChannel.sendMessage({
        text: text.trim(),
        attachments: uploadedAttachments.length ? uploadedAttachments : undefined,
      })
      // 不手动 push，由 message.new 事件统一插入，避免重复
    },

    // ─── 工具 ───────────────────────────────────────────────────────────────────

    /** 获取频道对方用户（1v1 场景） */
    getChannelPeer(
      channel: ChatChannel,
    ): { id: string; name: string; image?: string; company?: string; company_id?: string } | null {
      if (!_client?.userID) return null
      const members = Object.values(channel.state.members)
      console.log('members', channel, members)
      const peer = members.find(m => m.user?.id !== _client!.userID)
      console.log('peer', peer)
      if (!peer?.user) return null
      const u = peer.user as unknown as Record<string, unknown>
      return {
        id: peer.user.id,
        name: peer.user.name ?? peer.user.id,
        image: u.image as string | undefined,
        company: u.company as string | undefined,
        // 服务端写在用户上的 company_id 是 number，而合作名单是按字符串存进 Set 的，
        // 这里必须走 normalize 转成同一种类型，否则 Set.has(number) 恒为 false，
        // 会把所有会话都误判成「已停止合作」
        company_id: normalizeCoopCompanyId(u.company_id),
      }
    },

    getChannelUnread(channel: ChatChannel): number {
      return channel.countUnread()
    },

    /**
     * 会话里「对方的公司」，展示和合作校验都用它。
     * 优先读频道上固化的值（发起会话时按联系人列表写入，稳定）；
     * 老频道没有这些字段时，才回退到对方 Stream 用户对象上的 company（服务端写入，可能缺失或过时）
     */
    getChannelPeerCompany(channel: ChatChannel): { id: string; name: string } {
      const data = (channel.data ?? {}) as Partial<ChannelCompanyData>
      const isBiz = useUserStore().userSystem === System.business
      const id = isBiz ? data.agency_id : data.enterprise_id
      const name = isBiz ? data.agency_name : data.enterprise_name
      if (id || name) {
        return { id: normalizeCoopCompanyId(id), name: name ?? '' }
      }
      const peer = this.getChannelPeer(channel)
      return { id: peer?.company_id ?? '', name: peer?.company ?? '' }
    },

    getLastMessage(channel: ChatChannel): string {
      const msgs = channel.state.messages
      if (!msgs || msgs.length === 0) return ''
      const last = msgs[msgs.length - 1]
      if (last.text) return last.text.length > 28 ? last.text.slice(0, 28) + '…' : last.text
      if (last.attachments?.length) return `[${i18n.global.t('chat.file')}]`
      return ''
    },

    formatTime(dateStr: string | Date | undefined): string {
      if (!dateStr) return ''

      // 将 "2026-02-03 17:43:39" 格式转换为 Date 对象
      // 如果已经是 ISO 格式，直接使用；否则转换为 ISO 格式
      // if (dateStr.includes(' ') && !timeStr.includes('T')) {
      //   // 格式：2026-02-03 17:43:39 -> 2026-02-03T17:43:39
      //   dateStr = timeStr.replace(' ', 'T')
      // }

      const now = new Date()
      const time = new Date(dateStr)

      // 检查日期是否有效
      if (isNaN(time.getTime())) {
        return dateStr // 如果解析失败，返回原始字符串
      }

      // 获取今天的日期（年-月-日，忽略时间）
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
      // 获取消息的日期（年-月-日，忽略时间）
      const messageDate = new Date(time.getFullYear(), time.getMonth(), time.getDate())

      // 计算日期差（基于日期，而不是时间）
      const diffMs = today.getTime() - messageDate.getTime()
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

      const formatTime = (date: Date) => {
        const hours = date.getHours().toString().padStart(2, '0')
        const minutes = date.getMinutes().toString().padStart(2, '0')
        return `${hours}:${minutes}`
      }

      const formatFullDate = (date: Date) => {
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        // const hours = date.getHours().toString().padStart(2, '0')
        // const minutes = date.getMinutes().toString().padStart(2, '0')
        // return `${year}-${month}-${day} ${hours}:${minutes}`
        return `${month}-${day}`
      }

      // 今天
      if (diffDays === 0) {
        return formatTime(time)
      }
      // 昨天
      // else if (diffDays === 1) {
      //   return `昨天 ${formatTime(time)}`
      // }
      // 前天
      // else if (diffDays === 2) {
      //   return `前天 ${formatTime(time)}`
      // }
      // 3-7天
      else if (diffDays >= 1 && diffDays <= 7) {
        return `${diffDays}日前`
      }
      // 超过7天小于1年
      else if (diffDays < 365) {
        return formatFullDate(time)
      }
      // 超过1年
      else {
        const years = Math.floor(diffDays / 365)
        return i18n.global.t('chat.yearsAgo', [years])
      }
    },
  },
})
